import parseISO from 'date-fns/parseISO'
import startOfMonth from 'date-fns/startOfMonth'
import startOfDay from 'date-fns/startOfDay'
import addDays from 'date-fns/addDays'
import subDays from 'date-fns/subDays'
import addMonths from 'date-fns/addMonths'
import isToday from 'date-fns/isToday'
import isYesterday from 'date-fns/isYesterday'
import isSameDay from 'date-fns/isSameDay'
import isSameMonth from 'date-fns/isSameMonth'
import isValid from 'date-fns/isValid'
import lastDayOfMonth from 'date-fns/lastDayOfMonth'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import getDaysInMonth from 'date-fns/getDaysInMonth'
import localeJa from 'date-fns/locale/ja'
import isEqual from 'date-fns/isEqual'
import isPast from 'date-fns/isPast'
import isSaturday from 'date-fns/isSaturday'
import isSunday from 'date-fns/isSunday'
import differenceInDays from 'date-fns/differenceInDays'

const CURRENT_YEAR = new Date().getFullYear()

export default {
  /**
   * @see https://date-fns.org/v2.16.1/docs/parseISO
   */
  parseISO,

  /**
   * @see https://date-fns.org/v2.16.1/docs/format
   */
  format,

  /**
   * @see https://date-fns.org/v2.16.1/docs/I18n
   */
  localeJa,

  /**
   * @see https://date-fns.org/v2.16.1/docs/startOfMonth
   */
  startOfMonth,

  /**
   * @see https://date-fns.org/v2.16.1/docs/startOfDay
   */
  startOfDay,

  /**
   * @see https://date-fns.org/v2.16.1/docs/addDays
   */
  addDays,

  /**
   * @see https://date-fns.org/v2.16.1/docs/subDays
   */
  subDays,

  /**
   * @see https://date-fns.org/v2.16.1/docs/addMonths
   */
  addMonths,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isToday
   */
  isToday,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isYesterday
   */
  isYesterday,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isSameDay
   */
  isSameDay,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isSameMonth
   */
  isSameMonth,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isValid
   */
  isValid,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isEqual
   */
  isEqual,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isPast
   */
  isPast,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isSaturday
   */
  isSaturday,

  /**
   * @see https://date-fns.org/v2.16.1/docs/isSunday
   */
  isSunday,

  /**
   * @see https://date-fns.org/v2.16.1/docs/differenceInDays
   */
  differenceInDays,

  /**
   * 時刻が過ぎているどうか
   *
   * @param { Date } target 精査対象
   * @param { Date } compare しきい値
   * @return { boolean }
   */
  isAfter: (target: Date, compare: Date) => {
    return target.getTime() > compare.getTime()
  },

  /**
   * 時刻が過ぎている、もしくは同時刻かどうか
   *
   * @param { Date } target 精査対象
   * @param { Date } compare しきい値
   * @return { boolean }
   */
  isAfterOrEqual: (target: Date, compare: Date) => {
    return target.getTime() >= compare.getTime()
  },

  /**
   * 時刻前かどうか
   *
   * @param { Date } target 精査対象
   * @param { Date } compare しきい値
   * @return { boolean }
   */
  isBefore: (target: Date, compare: Date) => {
    return target.getTime() < compare.getTime()
  },

  /**
   * 時刻前か、もしくは同時刻かどうか
   *
   * @param { Date } target 精査対象
   * @param { Date } compare しきい値
   * @return { boolean }
   */
  isBeforeOrEqual: (target: Date, compare: Date) => {
    return target.getTime() <= compare.getTime()
  },

  /**
   * @see https://date-fns.org/v1.29.0/docs/getDaysInMonth
   */
  getDaysInMonth,

  /**
   * @see https://date-fns.org/v1.29.0/docs/lastDayOfMonth
   */
  lastDayOfMonth,

  /**
   * @see https://date-fns.org/v1.29.0/docs/parse
   */
  parse,

  /**
   * 当年から指定した長さの未来年リストを取得する(当年を含む)
   *
   * @param { number } length 取得するリスト数
   * @return { Array<number> } 年リスト
   *
   * @example 当年が 2023 年の場合: getFutureYearList(3) -> [2023, 2024, 2025]
   */
  getFutureYearList: (length: number = 0) => {
    return Array.from({ length }, (k, v) => CURRENT_YEAR + v)
  },

  /**
   * 当年から指定した長さの過去年リストを取得する(当年を含む)
   *
   * @param { number } length 取得するリスト数
   * @return { Array<number> } 年リスト
   *
   * @example 当年が 2023 年の場合: getPastYearList(3) -> [2023, 2022, 2021]
   */
  getPastYearList: (length: number = 0) => {
    return Array.from({ length }, (k, v) => CURRENT_YEAR - v)
  },

  /**
   * 時刻情報にタイムゾーンの情報がない場合に付与する
   *
   * @param { String } time 時刻情報を文字列
   * @return { String } タイムゾーン情報が付与された文字列時刻表記
   */
  normalizeDate,
}

/**
 * サーバサイドから時差の情報が付与されていない場合は日本時差の情報を付与する
 * (これがないと ios で 時差分時刻がずれる不具合が発生する)
 */
function normalizeDate(time: string): string {
  // 日本時刻に合わせる
  return time.endsWith('+09:00') ? time : `${time}+09:00`
}
